import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  UserGroupIcon,
  BriefcaseIcon,
  CurrencyYenIcon,
  ArrowTrendingUpIcon,
  EyeIcon,
  CalendarIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { formatCurrency, formatDate } from '../../utils/formatUtils';
import { API_BASE } from '../../lib/config';

const Analytics = ({ onBack }) => {
  const [dateRange, setDateRange] = useState('30days');
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState({
    overview: {
      totalUsers: 0,
      totalJobs: 0,
      totalRevenue: 0,
      activeUsers: 0
    },
    userStats: {
      newUsers: [],
      userTypes: {
        workers: 0,
        employers: 0
      }
    },
    jobStats: {
      completedJobs: [],
      jobCategories: {}
    },
    revenueStats: {
      monthlyRevenue: []
    }
  });

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange]);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('admin_token');

      // 1. Overview Stats
      const resOverview = await fetch(`${API_BASE}/admin/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const overviewData = resOverview.ok ? await resOverview.json() : {};

      // 2. Charts Data
      const resCharts = await fetch(`${API_BASE}/admin/analytics/charts?period=${dateRange}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const chartsData = resCharts.ok ? await resCharts.json() : { labels: [], revenue: [], users: [], jobs: [] };

      setAnalyticsData({
        overview: {
          totalUsers: overviewData.total_users || 0,
          totalJobs: overviewData.total_jobs || 0,
          totalRevenue: overviewData.total_revenue || 0,
          activeUsers: overviewData.total_users || 0
        },
        userStats: {
          newUsers: chartsData.users || [],
          labels: chartsData.labels || [],
          userTypes: {
            workers: overviewData.total_job_seekers || 0,
            employers: overviewData.total_employers || 0
          }
        },
        jobStats: {
          completedJobs: chartsData.jobs || [],
          labels: chartsData.labels || [],
          jobCategories: {}
        },
        revenueStats: {
          monthlyRevenue: chartsData.revenue || [],
          labels: chartsData.labels || []
        }
      });

    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 戻るボタン */}
      {onBack && (
        <div className="flex items-center">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            メニューに戻る
          </button>
        </div>
      )}

      {/* ヘッダー */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">アナリティクス</h2>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="7days">過去7日間</option>
          <option value="30days">過去30日間</option>
          <option value="90days">過去90日間</option>
          <option value="1year">過去1年間</option>
        </select>
      </div>

      {/* 概要カード */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <UserGroupIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">総ユーザー数</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.overview.totalUsers.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <BriefcaseIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">総求人数</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.overview.totalJobs.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CurrencyYenIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">総売上</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(analyticsData.overview.totalRevenue)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ArrowTrendingUpIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">アクティブユーザー</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.overview.activeUsers.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* グラフエリア */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ユーザー登録推移 */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">ユーザー登録推移</h3>
          <div className="h-64 flex items-end justify-between space-x-2">
            {analyticsData.userStats.newUsers.map((data, index) => (
              <div key={index} className="flex flex-col items-center flex-1">
                <div className="w-full bg-gray-200 rounded-t relative" style={{ height: '200px' }}>
                  <div
                    className="bg-blue-500 rounded-t absolute bottom-0 w-full"
                    style={{ height: `${(data.count / 50) * 100}%` }}
                  ></div>
                </div>
                <div className="mt-2 text-xs text-gray-600">{formatDate(data.date)}</div>
                <div className="text-xs text-gray-500">{data.count}人</div>
              </div>
            ))}
          </div>
        </div>

        {/* 完了した仕事推移 */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">完了した仕事推移</h3>
          <div className="h-64 flex items-end justify-between space-x-2">
            {analyticsData.jobStats.completedJobs.map((data, index) => (
              <div key={index} className="flex flex-col items-center flex-1">
                <div className="w-full bg-gray-200 rounded-t relative" style={{ height: '200px' }}>
                  <div
                    className="bg-green-500 rounded-t absolute bottom-0 w-full"
                    style={{ height: `${(data.count / 80) * 100}%` }}
                  ></div>
                </div>
                <div className="mt-2 text-xs text-gray-600">{formatDate(data.date)}</div>
                <div className="text-xs text-gray-500">{data.count}件</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* カテゴリ別統計 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ユーザータイプ分布 */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">ユーザータイプ分布</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">ワーカー</span>
              <span className="text-sm text-gray-500">{analyticsData.userStats.userTypes.workers}人</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full"
                style={{
                  width: `${(analyticsData.userStats.userTypes.workers + analyticsData.userStats.userTypes.employers) > 0
                    ? (analyticsData.userStats.userTypes.workers / (analyticsData.userStats.userTypes.workers + analyticsData.userStats.userTypes.employers)) * 100
                    : 0}%`
                }}
              ></div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">事業者</span>
              <span className="text-sm text-gray-500">{analyticsData.userStats.userTypes.employers}人</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full"
                style={{
                  width: `${(analyticsData.userStats.userTypes.workers + analyticsData.userStats.userTypes.employers) > 0
                    ? (analyticsData.userStats.userTypes.employers / (analyticsData.userStats.userTypes.workers + analyticsData.userStats.userTypes.employers)) * 100
                    : 0}%`
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* 求人カテゴリ分布 */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">求人カテゴリ分布</h3>
          <div className="space-y-3">
            {Object.entries(analyticsData.jobStats.jobCategories).map(([category, count]) => (
              <div key={category} className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">{category}</span>
                <span className="text-sm text-gray-500">{count}件</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 売上推移 */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">月間売上推移</h3>
        <div className="h-64 flex items-end justify-between space-x-2">
          {analyticsData.revenueStats.monthlyRevenue.map((data, index) => (
            <div key={index} className="flex flex-col items-center flex-1">
              <div className="w-full bg-gray-200 rounded-t relative" style={{ height: '200px' }}>
                <div
                  className="bg-purple-500 rounded-t absolute bottom-0 w-full"
                  style={{ height: `${(data.amount / 3000000) * 100}%` }}
                ></div>
              </div>
              <div className="mt-2 text-xs text-gray-600">{data.month}</div>
              <div className="text-xs text-gray-500">{formatCurrency(data.amount)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Analytics;